/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    className = "className",
    columns = "columns",
    gap = "gap",
    resizeRows = "resizeRows",
    collapseOnMobile = "collapseOnMobile",
    reverseOrderOnMobile = "reverseOrderOnMobile",
    background = "background",
    image = "image",
    gradientColorTop = "gradientColorTop",
    gradientColorBottom = "gradientColorBottom",
    backgroundColor = "backgroundColor",
    fullWidthRow = "fullWidthRow",
    margins = "margins",
    paddings = "paddings",
}

interface Props extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.resizeRows]: boolean;
        [fields.collapseOnMobile]: boolean;
        [fields.reverseOrderOnMobile]: boolean;
        [fields.background]: "image" | "color" | "gradient";
        [fields.image]: string;
        [fields.gradientColorTop]: string;
        [fields.gradientColorBottom]: string;
        [fields.backgroundColor]: string;
        [fields.fullWidthRow]: boolean;
        [fields.margins]: string;
        [fields.paddings]: string;
    };
}

const Container = styled('div')<{gap: number, collapse: boolean, reverse: boolean, background: string, image: string, gradientColorTop: string, gradientColorBottom: string, backgroundColor: string, fullWidthRow: boolean, margins: string, paddings: string}>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    margin:  ${p => `${p.margins}`};
    padding:  ${p => `${p.paddings}`};
    gap: ${p => `${p.gap  }px`};
    ${p => {
        if (p.background === "image") {
            return css`
            background-image: url(${p.image});
            background-size: cover;
            `;
        }  else if(p.background === "gradient"){
            {
                return css`
                    background-image: linear-gradient(${p.gradientColorTop}, ${p.gradientColorBottom});
                `;
            }
        } else {
            return css`
            background-color: ${p.backgroundColor};
            `;
        }
    }}
    ${({ theme, reverse }) => 
        (breakpointMediaQueries(theme, 
        [css`flex-direction: ${reverse ? 'column-reverse' : 'column'};`,
        css`flex-direction:  ${reverse ? 'column-reverse' : 'column'};`]))};
    ${p => {
        if (!p.collapse) {
            return css`
                flex-direction: row !important;
            `;
        }
    }}
    ${({ theme, fullWidthRow }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(theme, [
            css`
                max-width: ${fullWidthRow ? '100%' : `${maxWidths[1]}px`};
            `,
            css`
                max-width: ${fullWidthRow ? '100%' : `${maxWidths[1]}px`};
            `,
            css`
                max-width: ${fullWidthRow ? '100%' : `${maxWidths[2]}px`};
            `,
            css`
                max-width: ${fullWidthRow ? '100%' : `${maxWidths[3]}px`};
            `,
            css`
                max-width: ${fullWidthRow ? '100%' : `${maxWidths[4]}px`};
            `,
        ]);
    }}
`;

const RowItem = styled('div')<{width: number, resize: boolean}>`
    ${p => {
        if (!p.resize) {
            return css`
                width: ${`${p.width  }%`};
            `;
        } 
            return css`
                display: inline-flex;
                flex-basis: ${`${p.width  }%`};
                flex-grow: 1;
                flex-shrink: 1;
            `;
        
    }}
    ${({ theme }) => 
        (breakpointMediaQueries(theme, 
        [css`width: 100%;`,
        css`width:100%;`]))};
`;

const ADIFullWidthRow: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { className, columns, gap, resizeRows, collapseOnMobile, reverseOrderOnMobile, background, image, gradientColorTop, gradientColorBottom, backgroundColor, fullWidthRow, margins, paddings },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        columnElements.push(
            <RowItem key={x} width={(columns[x]/12)*100} resize={resizeRows}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </RowItem>,
        );
    }

    return <Container 
        className={className}
        collapse={collapseOnMobile === undefined ? true : collapseOnMobile} 
        gap={gap || 0} 
        reverse={reverseOrderOnMobile} 
        background={background}
        image={image}
        gradientColorTop={gradientColorTop}
        gradientColorBottom={gradientColorBottom}
        backgroundColor={backgroundColor || "#fff"}
        margins={margins}
        paddings={paddings}
        fullWidthRow={fullWidthRow}>
            {columnElements}
        </Container>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: ADIFullWidthRow,
    definition: {
        group: "Basic",
        icon: "Row",
        displayName: "ADI Full Width Row",
        fieldDefinitions: [
            {
                name: fields.className,
                displayName: "ClassName",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType:"Translatable",
            },
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.resizeRows,
                displayName: "Resize rows based on content",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General"
            },
            {
                name: fields.collapseOnMobile,
                displayName: "Collapse row on mobile",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                name: fields.reverseOrderOnMobile,
                displayName: "Reverse order on mobile",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                name: fields.fullWidthRow,
                displayName: "Full Width Row",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                    { displayName: "Gradient", value: "gradient" },
                ],
                defaultValue: "color",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            
            {
                name: fields.gradientColorTop,
                displayName: "Gradient Color Top",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "gradient",
            },
            {
                name: fields.gradientColorBottom,
                displayName: "Gradient Color Bottom",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "gradient",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: fields.margins,
                displayName: "Margins",
                editorTemplate: "TextField",
                defaultValue: "0px auto",
                fieldType:"General",
                placeholder: "top left bottom right",
            },
            {
                name: fields.paddings,
                displayName: "Paddings",
                editorTemplate: "TextField",
                defaultValue: "0px 0px 0px 0px",
                fieldType:"General",
                placeholder: "top left bottom right",
            },
        ],
    },
};

export default widgetModule;
